import cx from 'clsx';
import { Button, Icon } from '@vechaiui/react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/solid';
import { useLocalization } from '@fluent/react';
import { useQuery } from '@apollo/client';

import { GetOrdersCountByStatusesDocument, OrderStatus } from '#/__generated__/graphql/graphql';
import { PrivateRoute } from '#/constants/page-route';
import { SearchParam } from '#/constants/search-params';
import { useManagingProfile } from '#/hooks';

const BookingSummaryPanel = () => {
  const managingProfile = useManagingProfile();
  const navigate = useNavigate();
  const { l10n } = useLocalization();

  const { data: allOrders } = useQuery(GetOrdersCountByStatusesDocument, {
    variables: { orderStatuses: Object.values(OrderStatus) },
  });
  const { data: activeOrders } = useQuery(GetOrdersCountByStatusesDocument, {
    variables: {
      orderStatuses: [OrderStatus.OrderInTransit, OrderStatus.OrderProcessing],
    },
  });
  const { data: pendingOrders } = useQuery(GetOrdersCountByStatusesDocument, {
    variables: {
      // Temporary add PaymentDue to the status filter, but we need to check if it's creditterm only include
      orderStatuses: [OrderStatus.OrderPending, OrderStatus.OrderPaymentDue],
    },
  });
  const { data: deliveredOrders } = useQuery(GetOrdersCountByStatusesDocument, {
    variables: {
      orderStatuses: [OrderStatus.OrderDelivered],
    },
  });

  const handleNewTripRequestButtonPressed = () => {
    navigate(PrivateRoute.RequestNew);
  };

  return (
    <div>
      <header className="mb-6 text-sm text-gray-400 uppercase">{l10n.getString('orders')}</header>

      <div className="space-y-3">
        <Link className="flex space-x-3 text-neutral-700" to={PrivateRoute.OrderList}>
          <OrderData
            bgColour="bg-neutral-500"
            count={allOrders?.orders?.totalCount ?? 0}
            orderStatusLabel={'All Orders'}
          />
        </Link>
        <Link
          className="flex space-x-3 text-neutral-700"
          to={`${PrivateRoute.OrderList}?${SearchParam.Statuses}=processing,in_transit`}
        >
          <OrderData
            bgColour="bg-primary-500"
            count={activeOrders?.orders?.totalCount ?? 0}
            orderStatusLabel={l10n.getString('active-trips')}
          />
        </Link>
        <Link
          className="flex space-x-3 text-neutral-700"
          to={`${PrivateRoute.OrderList}?${SearchParam.Statuses}=pending,payment_due`}
        >
          <OrderData
            bgColour="bg-yellow-500"
            count={pendingOrders?.orders?.totalCount ?? 0}
            orderStatusLabel={l10n.getString('pending-requests')}
          />
        </Link>
        <Link
          className="flex space-x-3 text-neutral-700"
          to={`${PrivateRoute.OrderList}?${SearchParam.Statuses}=delivered`}
        >
          <OrderData
            bgColour="bg-green-500"
            count={deliveredOrders?.orders?.totalCount ?? 0}
            orderStatusLabel={l10n.getString('delivered')}
          />
        </Link>
      </div>

      {managingProfile?.__typename === 'Customer' && (
        <footer className="mt-6 2xl:mt-8">
          <Button
            className="w-full rounded-full"
            rightIcon={
              <Icon
                as={PlusIcon}
                className="w-6 h-6 ml-4 rounded-full border border-blue-500 text-blue-500 p-0.5"
                label="add"
              />
            }
            onClick={handleNewTripRequestButtonPressed}
            size="lg"
          >
            <div className="text-black">{l10n.getString('submit-trip-request')}</div>
          </Button>
        </footer>
      )}
    </div>
  );
};

const OrderData: React.FC<OrderDataProps> = ({ bgColour, count, orderStatusLabel }) => {
  return (
    <div className="flex justify-between w-full">
      <span>{orderStatusLabel}</span>
      <span className={cx('px-2 py-1 rounded-md text-white text-sm', bgColour)}>{count}</span>
    </div>
  );
};
type OrderDataProps = {
  bgColour: string;
  count: number;
  orderStatusLabel: string;
};
export default BookingSummaryPanel;
